import {useCallback, useEffect, useRef, useState} from 'react';
import {getCurrentFirmId, getCurrentUserId} from "../lib/picos/currentLogin";

// Define the enum for storage types
export enum LocalStorageType {
    string = 'string',
    number = 'number',
    boolean = 'boolean',
    object = 'object',
    array = 'array',
    json = 'json',
}

export enum KeySpecificity {
    None = 0,
    FirmSpecific = 1,
    UserSpecific = 2,
}

// Utility type to map LocalStorageType to TypeScript types
type StorageTypeMap<T extends LocalStorageType> =
    T extends LocalStorageType.string ? string :
    T extends LocalStorageType.number ? number :
    T extends LocalStorageType.boolean ? boolean :
    T extends LocalStorageType.object ? Record<string, any> :
    T extends LocalStorageType.array ? any[] :
    T extends LocalStorageType.json ? any :
    never;

// Helper functions for serialization/deserialization based on type
const serialize = <T>(value: T, type: LocalStorageType): string => {
    switch (type) {
        case LocalStorageType.string:
            return value as unknown as string;
        case LocalStorageType.number:
            return value?.toString() || '';
        case LocalStorageType.boolean:
            return (value as unknown as boolean) ? 'true' : 'false';
        case LocalStorageType.object:
        case LocalStorageType.array:
        case LocalStorageType.json:
        default:
            return JSON.stringify(value);
    }
};

const deserialize = <T>(value: string | null, type: LocalStorageType, defaultValue?: T): T | undefined => {
    if (value === null) return defaultValue;

    switch (type) {
        case LocalStorageType.string:
            return value as unknown as T;
        case LocalStorageType.number:
            return Number(value) as unknown as T;
        case LocalStorageType.boolean:
            return (value === 'true') as unknown as T;
        case LocalStorageType.object:
        case LocalStorageType.array:
        case LocalStorageType.json:
        default:
            return JSON.parse(value);
    }
};

// Custom hook for syncing state with localStorage
export function useLocalStorage<E extends LocalStorageType, T = StorageTypeMap<E>>(
    originalKey: string,
    type: E,
    initialValue?: T,
    userSpecificKey: KeySpecificity = KeySpecificity.None
): [T | undefined, (value: T | ((val: T | undefined) => T)) => void] {
    let key = originalKey;
    if (userSpecificKey == KeySpecificity.FirmSpecific) key = `${key}-${getCurrentFirmId()}`;
    else if (userSpecificKey == KeySpecificity.UserSpecific) key = `${key}-${getCurrentFirmId()}-${getCurrentUserId()}`;

    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState<T | undefined>(() => {
        if (typeof window === 'undefined') {
            return initialValue;
        }

        // Get from local storage by key
        const item = window.localStorage.getItem(key);

        try {
            // Parse based on type or return initialValue if none
            return item ? deserialize<T>(item, type, initialValue) : initialValue;
        }
        catch (error) {
            // If error also return initialValue
            console.error(`Error reading ${type} localStorage key "${key}" in useLocalStorage:`, {error, item, initialValue});
            return initialValue;
        }
    });

    // Use a ref to track the current value so setValue can remain stable
    const storedValueRef = useRef(storedValue);
    useEffect(() => {
        storedValueRef.current = storedValue;
    }, [storedValue]);

    // Return a wrapped version of useState's setter function that
    // persists the new value to localStorage
    const setValue = useCallback((value: T | ((val: T | undefined) => T)) => {
        try {
            // Allow value to be a function so we have same API as useState
            const valueToStore =
                value instanceof Function ? value(storedValueRef.current) : value;

            // Save state
            setStoredValue(valueToStore);

            // Save to local storage
            if (typeof window !== 'undefined') {
                if (valueToStore === undefined) {
                    window.localStorage.removeItem(key);
                }
                else {
                    const serialized = serialize(valueToStore, type);
                    window.localStorage.setItem(key, serialized);

                    // Dispatch a custom event so other components using the same key can update
                    window.dispatchEvent(
                        new CustomEvent('localStorage', {detail: {key, value: valueToStore, type}})
                    );
                }
            }
        }
        catch (error) {
            console.error(`Error setting localStorage key "${key}":`, error);
        }
    }, [key, type]);

    // Listen for changes to this localStorage key in other components
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        // Handle storage changes from other components
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === key) {
                if (event.newValue !== null) {
                    try {
                        const newValue = deserialize<T>(event.newValue, type, initialValue);
                        setStoredValue(newValue);
                    }
                    catch (error) {
                        console.error(`Error parsing ${type} localStorage value for key "${key}" in handleStorageChange:`, {error, item: event.newValue, initialValue});
                    }
                }
                else {
                    // Item was removed
                    setStoredValue(initialValue);
                }
            }
        };

        // Handle custom events from the same window
        const handleCustomEvent = (event: CustomEvent) => {
            const {key: eventKey, value, type: eventType} = event.detail;
            if (eventKey === key) {
                if (eventType === type) {
                    // If the types match, we can use the value directly
                    setStoredValue(value);
                }
                else {
                    // If types don't match, we need to re-read from localStorage
                    const item = window.localStorage.getItem(key);

                    try {
                        const newValue = deserialize<T>(item, type, initialValue);
                        setStoredValue(newValue);
                    }
                    catch(error) {
                        console.error(`Error parsing ${type} localStorage value for key "${key}" in handleCustomEvent:`, {error, value: item, initialValue});
                    }
                }
            }
        };

        // Listen for storage changes
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('localStorage' as any, handleCustomEvent);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('localStorage' as any, handleCustomEvent);
        };
    }, [initialValue, key, type]);

    return [storedValue, setValue];
}